import usePendingState from '../../Hooks/usePendingState';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';

import { faArrowLeft, faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNotification } from '../../Contexts/NotificationContext';
import Loading from '../Loading';

const FormEditCategory = () => {
    const { setNotification } = useNotification();
    const [loading, setLoading] = useState(true);
    const [types, setTypes] = useState([]);
    const [categoryData, setCategoryData] = useState({
        nome: '',
        tipo: '',
        destaque: false,
    });

    const [categoryErrorData, setCategoryErrorData] = useState({
        nome: [],
        tipo: [],
        destaque: [],
    });

    const [formLoading, setFormLoading, formPendingRef] = usePendingState();

    const navigate = useNavigate();
    const { id } = useParams();
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCategoryData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSelectChange = (name, selectedOption) => {
        setCategoryData((prevData) => ({
            ...prevData,
            [name]: selectedOption.value,
        }));
    };

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const [categoryResponse, typesResponse] = await Promise.all([
                    fetch(`${baseUrl}/manager/downloads/categorias/${id}`, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }),

                    fetch(`${baseUrl}/manager/downloads/tipos`, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }),
                ]);

                if (categoryResponse.ok) {
                    const categoryData = await categoryResponse.json();
                    setCategoryData(categoryData.categoria_download || []);
                } else {
                    setNotification({
                        type: 'error',
                        message: 'Erro ao buscar o categoria.',
                        show: true,
                    });
                }

                if (typesResponse.ok) {
                    const typesData = await typesResponse.json();
                    setTypes(
                        typesData.tipos_downloads.map((tipo) => ({
                            value: tipo.id,
                            label: tipo.nome,
                        }))
                    );
                } else {
                    setNotification({
                        type: 'error',
                        message: 'Erro ao buscar o categoria.',
                        show: true,
                    });
                }
            } catch (error) {
                setNotification({
                    type: 'error',
                    message: 'Erro na requisição',
                    show: true,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formPendingRef.current) return;
        setFormLoading(true);

        try {
            const response = await fetch(`${baseUrl}/manager/downloads/categorias/atualizar/${id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(categoryData),
            });
            if (response.ok) {
                setNotification({
                    type: 'success',
                    message: 'Categoria atualizada com sucesso!',
                    show: true,
                });
                navigate(`/manager/downloads/categorias/${categoryData.tipo}`);
            } else {
                if (response.status === 422) {
                    const errors = await response.json();

                    setCategoryErrorData(errors);
                } else {
                    throw new Error('Erro ao atualizar categoria.');
                }
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        } finally {
            setFormLoading(false);
        }
    };

    if (loading)
        return (
            <div className="mx-auto py-20 flex justify-center">
                <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent" />
            </div>
        );

    return (
        <>
            <Link
                to={`/manager/downloads/arquivos/${id}`}
                className="bg-secondary text-white px-4 py-1.5 rounded border border-secondary float-right ml-2 transition-all hover:bg-opacity-80"
            >
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                Arquivos
            </Link>

            <Link
                to={`/manager/downloads/categorias/${categoryData.tipo}`}
                className="bg-slate-100 px-4 py-1.5 rounded border border-slate-300 float-right ml-2 transition-all hover:bg-slate-200"
            >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Voltar
            </Link>

            <h4 className="mb-6 text-xl font-bold text-black">Editar categoria</h4>

            <div className="flex flex-col">
                <form onSubmit={handleSubmit} className="max-w-5xl pb-10">
                    <div className="mb-6 2xl:mb-10">
                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-3/4">
                                <label className="block text-sm font-medium mb-2">Nome</label>
                                <input
                                    type="text"
                                    name="nome"
                                    value={categoryData.nome}
                                    onClick={() => setCategoryErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {categoryErrorData.nome && categoryErrorData.nome[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {categoryErrorData.nome[0]}{' '}
                                        <span
                                            onClick={() => setCategoryErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-3/4">
                                <label className="block text-sm font-medium mb-2">Tipo de Categoria</label>
                                <Select
                                    name="tipo"
                                    options={types}
                                    value={types.find((option) => option.value === categoryData.tipo) || null}
                                    onClick={() => setCategoryErrorData((prevData) => ({ ...prevData, tipo: [] }))}
                                    onChange={(selectedOption) => handleSelectChange('tipo', selectedOption)}
                                    placeholder="Selecione uma opção..."
                                    classNamePrefix="admin-select"
                                    className="category-select"
                                />
                                {categoryErrorData.tipo && categoryErrorData.tipo[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {categoryErrorData.tipo[0]}{' '}
                                        <span
                                            onClick={() => setCategoryErrorData((prevData) => ({ ...prevData, tipo: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={formLoading}
                        aria-busy={formLoading}
                        className="w-full lg:w-3/4 bg-secondary text-white py-2 px-4 rounded-md hover:bg-opacity-80 disabled:bg-opacity-80 disabled:cursor-not-allowed"
                    >
                        {!formLoading ? 'Salvar' : <Loading />}
                    </button>
                </form>
            </div>
        </>
    );
};

export default FormEditCategory;
